import React, { useState, useCallback } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Button, Card, CardBody, Skeleton, Chip } from '@heroui/react';
import { Plus, Edit, Trash2, X, Save, ChevronRight, ChevronDown, FolderTree } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FormInput, FormTextarea } from './form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ConfirmationModal from './ConfirmationModal';
import CategorySelector from './selectors/CategorySelector';

export interface Category {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  parent?: Category;
  children?: Category[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CategoryFormData {
  name: string;
  description?: string;
  parentId?: string;
}

const categorySchema = z.object({
  name: z.string().min(1, 'Name is required').max(256, 'Name must be less than 256 characters'),
  description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
  parentId: z.string().uuid().optional().or(z.literal('')),
});

interface CategoryManagerProps {
  categories: Category[]; // Tree structure with nested children
  isLoading?: boolean;
  isSubmitting?: boolean;
  onAddSubmit: (data: CategoryFormData) => Promise<void>;
  onEditSubmit: (id: string, data: CategoryFormData) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({
  categories,
  isLoading = false,
  onAddSubmit,
  onEditSubmit,
  onDelete,
  isSubmitting = false,
}) => {
  const { t } = useTranslation('admin');
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Get all descendant IDs recursively from nested children
  const getDescendantIds = useCallback((category: Category): string[] => {
    const descendants: string[] = [];
    const collectDescendants = (cat: Category) => {
      if (cat.children && cat.children.length > 0) {
        cat.children.forEach((child) => {
          descendants.push(child.id);
          collectDescendants(child);
        });
      }
    };
    collectDescendants(category);
    return descendants;
  }, []);

  const {
    control: addControl,
    handleSubmit: handleAddSubmit,
    reset: resetAddForm,
    watch: watchAdd,
    setValue: setAddValue,
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      description: '',
      parentId: '',
    },
  });

  const selectedParentId = watchAdd('parentId');

  const handleAddClick = () => {
    setShowAddForm(true);
    resetAddForm();
  };

  const handleAddFormSubmit: SubmitHandler<CategoryFormData> = async (data) => {
    const submitData = {
      ...data,
      parentId: data.parentId || undefined,
    };
    await onAddSubmit(submitData);
    resetAddForm();
    setShowAddForm(false);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    resetAddForm();
  };

  const handleDeleteClick = (id: string) => {
    setCategoryToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (categoryToDelete) {
      await onDelete(categoryToDelete);
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
    }
  };

  const toggleExpand = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Skeleton className="h-10 w-32 rounded-lg" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex justify-end">
        <Button
          color="primary"
          startContent={<Plus className="w-4 h-4" />}
          onPress={handleAddClick}
          isDisabled={showAddForm}
        >
          {t('categories.addButton')}
        </Button>
      </div>

      {/* Add Category Form */}
      {showAddForm && (
        <Card className="border-2 border-primary-200">
          <CardBody className="p-4">
            <form onSubmit={handleAddSubmit(handleAddFormSubmit)} className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-4">
                  <FormInput
                    name="name"
                    control={addControl}
                    label={t('categories.addForm.name.label')}
                    placeholder={t('categories.addForm.name.placeholder')}
                    isRequired
                  />
                  <FormTextarea
                    name="description"
                    control={addControl}
                    label={t('categories.addForm.description.label')}
                    placeholder={t('categories.addForm.description.placeholder')}
                    minRows={2}
                  />
                  <CategorySelector
                    label={t('categories.addForm.parent.label', { defaultValue: 'Parent Category' })}
                    placeholder={t('categories.addForm.parent.placeholder', {
                      defaultValue: 'Select parent category (optional)',
                    })}
                    value={selectedParentId || ''}
                    onChange={(val) => setAddValue('parentId', (val as string) || '')}
                    variant="bordered"
                    showEmptyOption
                  />
                </div>
                <div className="flex gap-2 pt-8">
                  <Button
                    type="button"
                    variant="light"
                    onPress={handleCancelAdd}
                    isIconOnly
                    aria-label={t('categories.addForm.cancel')}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    isLoading={isSubmitting}
                    startContent={<Save className="w-4 h-4" />}
                  >
                    {t('categories.addForm.save')}
                  </Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {/* Categories List - Hierarchical View */}
      {categories.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <FolderTree className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg font-medium">{t('categories.empty.title')}</p>
          <p className="text-gray-400 text-sm mt-2">{t('categories.empty.message')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {categories.map((category) => (
            <CategoryTreeItem
              key={category.id}
              category={category}
              getDescendantIds={getDescendantIds}
              isExpanded={expandedCategories.has(category.id)}
              onToggleExpand={() => toggleExpand(category.id)}
              onDelete={handleDeleteClick}
              onEditSubmit={onEditSubmit}
              level={0}
              expandedCategories={expandedCategories}
              toggleExpand={toggleExpand}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t('categories.delete.title')}
        message={t('categories.delete.message')}
        confirmLabel={t('categories.delete.confirm')}
        cancelLabel={t('categories.delete.cancel')}
        confirmColor="danger"
      />
    </div>
  );
};

interface CategoryTreeItemProps {
  category: Category;
  getDescendantIds: (category: Category) => string[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDelete: (id: string) => void;
  onEditSubmit: (id: string, data: CategoryFormData) => Promise<void>;
  level: number;
  expandedCategories: Set<string>;
  toggleExpand: (id: string) => void;
}

const CategoryTreeItem: React.FC<CategoryTreeItemProps> = ({
  category,
  getDescendantIds,
  isExpanded,
  onToggleExpand,
  onDelete,
  onEditSubmit,
  level,
  expandedCategories,
  toggleExpand,
}) => {
  const { t } = useTranslation('admin');
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const childCategories = category.children || [];
  const hasChildren = childCategories.length > 0;

  const { control, handleSubmit, reset, watch, setValue } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: category.name,
      description: category.description || '',
      parentId: category.parentId || '',
    },
  });

  const selectedParentId = watch('parentId');

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleFormSubmit: SubmitHandler<CategoryFormData> = async (data) => {
    setIsSubmitting(true);
    try {
      const submitData = {
        ...data,
        parentId: data.parentId || undefined,
      };
      await onEditSubmit(category.id, submitData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset({
      name: category.name,
      description: category.description || '',
      parentId: category.parentId || '',
    });
    setIsEditing(false);
  };

  // Get IDs to exclude from parent options to prevent circular reference
  const excludeIds = [category.id, ...getDescendantIds(category)];

  if (isEditing) {
    return (
      <Card className="w-full border-2 border-primary-200" style={{ marginLeft: level * 24 }}>
        <CardBody className="p-4">
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-3">
            <FormInput
              name="name"
              control={control}
              label={t('categories.card.name.label')}
              placeholder={t('categories.card.name.placeholder')}
              isRequired
            />
            <FormTextarea
              name="description"
              control={control}
              label={t('categories.card.description.label')}
              placeholder={t('categories.card.description.placeholder')}
              minRows={2}
            />
            <CategorySelector
              label={t('categories.addForm.parent.label', { defaultValue: 'Parent Category' })}
              placeholder={t('categories.addForm.parent.placeholder', { defaultValue: 'Select parent category' })}
              value={selectedParentId || ''}
              onChange={(val) => setValue('parentId', (val as string) || '')}
              variant="bordered"
              showEmptyOption
              excludeIds={excludeIds}
            />
            <div className="flex gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="light"
                size="sm"
                onPress={handleCancel}
                isIconOnly
                aria-label={t('categories.card.cancel')}
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                type="submit"
                color="primary"
                size="sm"
                isLoading={isSubmitting}
                isIconOnly
                aria-label={t('categories.card.save')}
              >
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    );
  }

  return (
    <div style={{ marginLeft: level * 24 }}>
      <Card className="w-full hover:shadow-md transition-shadow">
        <CardBody className="p-4">
          <div className="space-y-2">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {hasChildren && (
                  <button
                    onClick={onToggleExpand}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                )}
                {!hasChildren && <div className="w-6" />}
                <div>
                  <h3 className="font-semibold text-lg text-foreground line-clamp-2">{category.name}</h3>
                  {hasChildren && (
                    <Chip size="sm" variant="flat" color="primary" className="mt-1">
                      {childCategories.length} {t('categories.subcategories', { defaultValue: 'subcategories' })}
                    </Chip>
                  )}
                </div>
              </div>
              <div className="flex gap-1 ml-2">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={handleEditClick}
                  aria-label={t('categories.card.edit')}
                >
                  <Edit className="w-4 h-4 text-gray-600" />
                </Button>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  onPress={() => onDelete(category.id)}
                  aria-label={t('categories.card.delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {category.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 ml-6">{category.description}</p>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Render children if expanded - using nested children directly */}
      {hasChildren && isExpanded && (
        <div className="mt-2 space-y-2">
          {childCategories.map((child) => (
            <CategoryTreeItem
              key={child.id}
              category={child}
              getDescendantIds={getDescendantIds}
              isExpanded={expandedCategories.has(child.id)}
              onToggleExpand={() => toggleExpand(child.id)}
              onDelete={onDelete}
              onEditSubmit={onEditSubmit}
              level={level + 1}
              expandedCategories={expandedCategories}
              toggleExpand={toggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
